import { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import FilterValuePopover from './FilterValuePopover';

const FilterCard = ({
  setTempFilters,
  outerIndex,
  innerIndex,
  propertyField,
  propertyOperator,
  propertyValue,
  fieldList = [],
  operatorList = [],
  booleanList = [],
  enumDataSources = {},
}) => {
  const [selectedField, setSelectedField] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('');
  const [selectedOperatorOptions, setSelectedOperatorOptions] = useState('');
  const [popoverFilterValues, setPopoverFilterValues] = useState([]);

  useEffect(() => {
    // Set initial selected values based on props
    setSelectedField(propertyField);

    if (propertyOperator) {
      const selectedOperatorType = operatorList.find(
        (operator) => operator.value === propertyOperator
      );
      if (selectedOperatorType) {
        const selectedOperators = operatorList.filter(
          (operator) => operator.type === selectedOperatorType.type
        );
        if (selectedOperators.length > 0) {
          setSelectedOperator(selectedOperatorType.type);
          setSelectedOperatorOptions(selectedOperators);
          updatePopoverItems(propertyField, selectedOperatorType.type);
        } else {
          setSelectedOperator('');
          setSelectedOperatorOptions('');
        }
      }
    }
  }, []);

  // Function to update the field values for popover items
  const updatePopoverItems = (popoverConditionField, selectedOperatorType) => {
    if (selectedOperatorType === 'enum') {
      // Use enumDataSources to get the appropriate data
      const dataSource = enumDataSources[popoverConditionField];
      if (dataSource) {
        setPopoverFilterValues(dataSource);
      }
    }
  };

  // Function to update the field value in the temp filters
  const updateFilterField = (newField) => {
    const selectedFieldInfo = fieldList.find(
      (field) => field.value === newField
    );

    setTempFilters((prevState) => {
      // Create a copy of the state array
      const newState = [...prevState];

      // Modify the field value of the filter at the specified innerIndex in the inner array at the specified outerIndex
      newState[outerIndex][innerIndex] = {
        field: newField,
        operator: '',
        value: selectedFieldInfo.type === 'enum' ? [] : '',
      };

      return newState;
    });

    setSelectedField(newField);

    if (selectedFieldInfo) {
      const selectedOperators = operatorList.filter(
        (operator) => operator.type === selectedFieldInfo.type
      );
      if (selectedOperators.length > 0) {
        setSelectedOperatorOptions(selectedOperators);
        setSelectedOperator('');
      } else {
        setSelectedOperator('');
        setSelectedOperatorOptions('');
      }
    }
  };

  // Function to update the operator value in the temp filters
  const updateFilterOperator = (newOperator) => {
    const selectedOperatorType = operatorList.find(
      (operator) => operator.value === newOperator
    );

    setTempFilters((prevState) => {
      // Create a copy of the state array
      const newState = [...prevState];

      // Modify the field value of the filter at the specified innerIndex in the inner array at the specified outerIndex
      newState[outerIndex][innerIndex] = {
        ...newState[outerIndex][innerIndex],
        operator: newOperator,
        value: selectedOperatorType.type === 'enum' ? [] : '',
      };

      return newState;
    });

    if (selectedOperatorType) {
      setSelectedOperator(selectedOperatorType.type);
      updatePopoverItems(selectedField, selectedOperatorType.type);
    } else {
      setSelectedOperator('');
    }
  };

  // Function to update the value in the temp filters
  const updateFilterValue = (newValue) => {
    setTempFilters((prevState) => {
      // Create a copy of the state array
      const newState = [...prevState];

      // Modify the field value of the filter at the specified innerIndex in the inner array at the specified outerIndex
      newState[outerIndex][innerIndex] = {
        ...newState[outerIndex][innerIndex],
        value: newValue,
      };

      return newState;
    });
  };

  // Function to update the value for boolean input in the temp filters
  const handleBooleanSelect = (newValue) => {
    setTempFilters((prevState) => {
      // Create a copy of the state array
      const newState = [...prevState];

      // Modify the field value of the filter at the specified innerIndex in the inner array at the specified outerIndex
      newState[outerIndex][innerIndex] = {
        ...newState[outerIndex][innerIndex],
        value: newValue,
      };

      return newState;
    });
  };

  // Function to update the value for text/number input in the temp filters
  const handleInputChange = (newValue) => {
    setTempFilters((prevState) => {
      // Create a copy of the state array
      const newState = [...prevState];

      // Modify the field value of the filter at the specified innerIndex in the inner array at the specified outerIndex
      newState[outerIndex][innerIndex] = {
        ...newState[outerIndex][innerIndex],
        value: newValue,
      };

      return newState;
    });
  };

  // Function to delete the filters from the temp filters
  const handleFilterDelete = () => {
    setTempFilters((prevState) => {
      // Create a copy of the state array
      const newState = [...prevState];

      newState[outerIndex] = newState[outerIndex].filter(
        (_, index) => index !== innerIndex
      );

      // If the inner array becomes empty, remove the outer array
      if (newState[outerIndex].length === 0) {
        newState.splice(outerIndex, 1);
      }

      return newState;
    });
  };

  return (
    <div className='flex cursor-default flex-row space-x-2 rounded-md border p-3 py-3 hover:bg-foreground/10'>
      <div className='flex flex-grow flex-col'>
        <div className='w-full'>
          <Select value={selectedField} onValueChange={updateFilterField}>
            <SelectTrigger className=''>
              <SelectValue placeholder='Select property' />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {fieldList.map((field) => (
                  <SelectItem key={field._id} value={field.value}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <div className='mt-2 flex flex-shrink flex-row space-x-1'>
          <div className='w-1/2'>
            <Select
              value={propertyOperator}
              onValueChange={updateFilterOperator}
              disabled={!propertyField}
            >
              <SelectTrigger className=''>
                <SelectValue placeholder='Select operator' />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {selectedOperatorOptions &&
                    selectedOperatorOptions.map((operator) => (
                      <SelectItem key={operator._id} value={operator.value}>
                        {operator.label}
                      </SelectItem>
                    ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className='w-1/2'>
            {selectedOperator === 'text' || selectedOperator === 'number' ? (
              <Input
                type={selectedOperator === 'text' ? 'text' : 'number'}
                placeholder='Value'
                onChange={(e) => handleInputChange(e.target.value)}
                value={propertyValue}
              />
            ) : selectedOperator === 'boolean' ? (
              <Select value={propertyValue} onValueChange={handleBooleanSelect}>
                <SelectTrigger>
                  <SelectValue placeholder='Select Value' />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {booleanList.map((option) => (
                      <SelectItem key={option._id} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            ) : selectedOperator === 'enum' ? (
              <FilterValuePopover
                filterValues={popoverFilterValues}
                updateFilterValue={updateFilterValue}
                selectedFilterValues={propertyValue}
                filterFieldName={propertyField}
              />
            ) : null}
          </div>
        </div>
      </div>
      <div className='flex items-center justify-center'>
        <Button variant='ghost' size='xs' onClick={handleFilterDelete}>
          <XIcon className='h-3 w-3' />
        </Button>
      </div>
    </div>
  );
};

export default FilterCard;
